import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "./Button";
import { cn } from "../cn";

/**
 * Confirm dialog for irreversible or consequential actions (delete, reject, submit
 * for review). Built on Radix Dialog so it is focus-trapped and Escape cancels for
 * free. Controlled — the caller owns `open` and runs the side effect in `onConfirm`.
 *
 * Overlays may carry elevation (house rule), so this uses `shadow-overlay`.
 */
export function AlertDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  tone = "default",
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: "default" | "danger";
  onConfirm: () => void;
}) {
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  function handleConfirm() {
    onConfirm();
    onOpenChange(false);
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content
          role="alertdialog"
          onOpenAutoFocus={(event) => {
            // Keep focus off the confirm button so a stray Enter cannot fire a
            // consequential action; land on the cancel button instead.
            event.preventDefault();
            cancelRef.current?.focus();
          }}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2",
            "rounded-card border border-line bg-card shadow-overlay animate-fade-in",
            "p-6",
          )}
        >
          <Dialog.Title className="text-base font-semibold tracking-tight text-ink">
            {title}
          </Dialog.Title>
          {description != null && (
            <Dialog.Description className="mt-2 text-sm leading-relaxed text-mute">
              {description}
            </Dialog.Description>
          )}
          <div className="mt-6 flex items-center justify-end gap-2">
            <Dialog.Close asChild>
              <Button ref={cancelRef} variant="secondary">
                {cancelLabel}
              </Button>
            </Dialog.Close>
            <Button variant={tone === "danger" ? "danger" : "primary"} onClick={handleConfirm}>
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
